import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronsUpDown, Check, type LucideIcon } from 'lucide-react';

interface DropdownItem {
    id?: string | number | null;
    label: string;
    value: any;
    [key: string]: any;
}

interface SingleSelectLocalSearchDropdownProps {
    label?: string;
    value?: string | number | null;
    options: DropdownItem[];
    onChange: (value: any) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    icon?: LucideIcon;
    searchPlaceholder?: string;
    searchInputPlaceholder?: string;
    error?: string;
    required?: boolean;
    searchFields?: string[];
    buttonClassName?: string;
}

const SingleSelectLocalSearchDropdown = React.memo<SingleSelectLocalSearchDropdownProps>(({
    label,
    value,
    options,
    onChange,
    disabled = false,
    placeholder = "Select item",
    className = "",
    icon: Icon,
    searchInputPlaceholder = "Search...",
    error,
    required,
    searchFields = ['label'],
    buttonClassName = ""
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const [searchTerm, setSearchTerm] = useState('');

    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const filteredItems = options.filter(item => {
        if (!searchTerm.trim()) return true;
        const lowerTerm = searchTerm.toLowerCase();
        return searchFields.some(field => {
            const val = item[field];
            return val && String(val).toLowerCase().includes(lowerTerm);
        });
    });

    const selectedItem = options.find(opt => opt.value === value);

    const calculateDropdownPosition = useCallback(() => {
        if (!buttonRef.current) return;
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownHeight = 320;
        const windowHeight = window.innerHeight;
        const spaceBelow = windowHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        let position: 'bottom' | 'top' = 'bottom';

        const menuWidth = Math.max(buttonRect.width, 250);
        const windowWidth = window.innerWidth;
        let left = buttonRect.left;

        // Ensure menu doesn't overflow right edge of screen
        if (left + menuWidth > windowWidth - 16) {
            left = Math.max(16, windowWidth - menuWidth - 16);
        }

        let style: React.CSSProperties = {
            width: `${menuWidth}px`,
            left: `${left}px`,
            position: 'fixed'
        };

        if (spaceBelow < dropdownHeight + 20 && spaceAbove > dropdownHeight + 20) {
            position = 'top';
            style.bottom = `${windowHeight - buttonRect.top + 8}px`;
        } else {
            position = 'bottom';
            style.top = `${buttonRect.bottom + 8}px`;
        }

        setDropdownPosition(position);
        setMenuStyle(style);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                dropdownRef.current && !dropdownRef.current.contains(target) &&
                menuRef.current && !menuRef.current.contains(target)
            ) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const updatePosition = useCallback(() => {
        if (isOpen) {
            calculateDropdownPosition();
        }
    }, [isOpen, calculateDropdownPosition]);

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);
        }
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, updatePosition]);

    const toggleDropdown = () => {
        if (disabled) return;
        if (!isOpen) {
            calculateDropdownPosition();
            setSearchTerm('');
        }
        setIsOpen(!isOpen);
    };

    const handleSelect = (item: DropdownItem) => {
        onChange(item.value);
        setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {label && (
                <label className="flex items-center gap-1.5 text-xs font-bold text-gray-200 mb-1">
                    {Icon && <Icon size={15} className='text-orange-500' />}
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}

            <button
                ref={buttonRef}
                type="button"
                onClick={toggleDropdown}
                disabled={disabled}
                className={`w-full flex items-center justify-between bg-slate-900 border ${error ? 'border-red-500' : 'border-gray-700/50'} rounded-lg px-3 py-2.5 h-11 text-sm text-white focus:border-orange-500 focus:bg-gray-800 outline-none transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-600'} ${buttonClassName}`}
            >
                <div className="flex items-center gap-2 overflow-hidden min-w-0 flex-1">
                    {selectedItem ? (
                        <span className="truncate text-white text-left w-full font-semibold">
                            {selectedItem.label}
                        </span>
                    ) : (
                        <span className="text-gray-500 truncate">{placeholder}</span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {value && !disabled && (
                        <X
                            onClick={handleClear}
                            className="w-3.5 h-3.5 text-gray-500 hover:text-gray-300 transition-colors"
                        />
                    )}
                    <ChevronsUpDown className="w-3.5 h-3.5 text-gray-500" />
                </div>
            </button>

            {isOpen && createPortal(
                <div
                    ref={menuRef}
                    className={`fixed z-[99999] bg-slate-900 border border-gray-700 rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100 ${dropdownPosition === 'top' ? 'origin-bottom' : 'origin-top'}`}
                    style={{ ...menuStyle, maxHeight: '320px' }}
                >
                    <div className="p-2 border-b border-gray-800 bg-slate-900/50 sticky top-0 z-10">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full bg-slate-800 border border-gray-700 rounded-md pl-8 pr-3 py-1.5 text-[12px] text-white focus:border-orange-500 outline-none transition-all"
                                placeholder={searchInputPlaceholder}
                                autoFocus
                            />
                        </div>
                    </div>

                    <div className="overflow-y-auto max-h-[260px] custom-scroll">
                        {filteredItems.length > 0 ? (
                            <div className="p-1">
                                {filteredItems.map((item, idx) => {
                                    const isSelected = item.value === value;
                                    return (
                                        <button
                                            key={`${item.value}-${idx}`}
                                            type="button"
                                            onClick={() => handleSelect(item)}
                                            className={`w-full flex items-center justify-between px-2.5 py-2 rounded-md text-[13px] transition-all group ${isSelected ? 'bg-orange-600/20 text-orange-400 font-medium' : 'text-gray-300 hover:bg-slate-800 hover:text-white'}`}
                                        >
                                            <span className="truncate w-full text-left">
                                                {item.label}
                                            </span>
                                            {isSelected && <Check className="w-4 h-4 shrink-0" />}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="p-6 text-center">
                                <p className="text-[12px] text-white font-semibold">No results found</p>
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}

            {error && <p className="mt-1 text-[10px] text-red-500 font-medium">{error}</p>}
        </div>
    );
});

SingleSelectLocalSearchDropdown.displayName = 'SingleSelectLocalSearchDropdown';

export default SingleSelectLocalSearchDropdown;
